import React, { useState } from "react";
import { Button, Col, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import classNames from "classnames";

import GameActionButtons from "./GameActionButtons";

import { selectSelectedTeam } from "../features/clubhouse/clubhouseSlice";
import { selectSelectedLeague } from "../features/leaguemgmt/leagueManagementSlice";
import { useAppSelector } from "../hooks/reduxHooks";
import useBreakpoint from "../hooks/useBreakpoint";
import { TEAM_LOGO_URL } from "../lib/globals";
import {
    canEditTeam,
    defaultDateFormat,
    defaultTimeFormat,
    isXOrBigger,
    isXOrSmaller,
} from "../lib/utils";
import { IGame, IGameTeam, ISiteSetting } from "../types/api";

const Schedule = ({
    schedule,
    displayDeleteLink,
    setSchedule,
    scheduleHeader,
    scheduleType,
    showButtons = false,
}: {
    schedule: IGame[] | null;
    displayDeleteLink?: ISiteSetting["setting"];
    setSchedule?: React.Dispatch<React.SetStateAction<IGame[]>>;
    scheduleHeader?: string;
    scheduleType: "team" | "league";
    showButtons?: boolean;
}): React.JSX.Element => {
    let newMonth: string;
    let newDay: string;
    const selectedTeam = useAppSelector(selectSelectedTeam);
    const selectedLeague = useAppSelector(selectSelectedLeague);
    const [showSummary, setShowSummary] = useState<
        Record<number, boolean> | undefined
    >(
        schedule?.reduce(
            (acc, g) => {
                acc[g.id] = false;
                return acc;
            },
            {} as Record<number, boolean>,
        ),
    );
    const [summary, setSummary] = useState<
        Record<number, IGameTeam["summary"]> | undefined
    >(
        schedule?.reduce(
            (acc, g) => {
                acc[g.id] = "";
                return acc;
            },
            {} as Record<number, IGameTeam["summary"]>,
        ),
    );

    const breakpoint = useBreakpoint();

    const handleDelete = (gameId: number) => {
        const newSchedule = schedule!.filter(g => g.id !== gameId);

        setSchedule && setSchedule(newSchedule);
    };

    const timeAndLocation = (s: IGame, gameTime: string) => (
        <td
            className="timeAndLocation"
            width={isXOrSmaller("md", breakpoint) ? undefined : "20%"}
            rowSpan={s.isPlayoff && isXOrBigger("lg", breakpoint) ? 2 : 1}
            colSpan={isXOrSmaller("md", breakpoint) ? 7 : undefined}
        >
            {s.isPlayoff && (
                <>
                    <b>PLAYOFF GAME</b>
                    <br />
                </>
            )}
            {gameTime}
            <br />@{" "}
            <Link to={`/fields/${s.fieldId}`} target="_blank">
                {s.fieldName}
            </Link>
            {s.diamond ? (
                <>
                    <br />
                    Diamond: {s.diamond}
                </>
            ) : (
                ""
            )}
            {s.note ? (
                <>
                    <br />
                    Field Note: {s.note}
                </>
            ) : (
                ""
            )}
        </td>
    );

    const teamScoreInfo = (s: IGame) => (
        <>
            <td className="visitor logo" width={"8%"}>
                {s.visitor.logo && (
                    <img
                        src={`${TEAM_LOGO_URL}${s.visitor.logo}`}
                        alt={`${s.visitor.name} logo`}
                        className="teamLogo"
                    />
                )}
            </td>
            <td className="visitor team" width={"20%"}>
                {s.visitor.id
                    ? s.isPlayoff
                        ? `#${selectedLeague?.standings?.[s.visitor.id].standing} ${s.visitor.name}`
                        : s.visitor.name
                    : s.isPlayoff
                      ? s.champGameInfo
                          ? s.champGameInfo?.visitor.seed
                              ? `Seed #${s.champGameInfo?.visitor.seed}`
                              : `Winner of Game #${s.champGameInfo?.visitor.winnerOf}`
                          : s.consGameInfo?.visitor.seed
                            ? `Seed #${s.consGameInfo?.visitor.seed}`
                            : `Winner of Game #${s.consGameInfo?.visitor.winnerOf}`
                      : ""}
            </td>
            <td className="visitor score" width={"8%"}>
                {s.visitor.score}
            </td>
            <td className="at" width={"8%"}>
                @
            </td>
            <td className="home logo" width={"8%"}>
                {s.home.logo && (
                    <img
                        src={`${TEAM_LOGO_URL}${s.home.logo}`}
                        alt={`${s.home.name} logo`}
                        className="teamLogo"
                    />
                )}
            </td>
            <td className="home team" width={"20%"}>
                {s.home.id
                    ? s.isPlayoff
                        ? `#${selectedLeague?.standings?.[s.home.id].standing} ${s.home.name}`
                        : s.home.name
                    : s.isPlayoff
                      ? s.champGameInfo
                          ? s.champGameInfo?.home.seed
                              ? `Seed #${s.champGameInfo?.home.seed}`
                              : `Winner of Game #${s.champGameInfo?.home.winnerOf}`
                          : s.consGameInfo?.home.seed
                            ? `Seed #${s.consGameInfo?.home.seed}`
                            : `Winner of Game #${s.consGameInfo?.home.winnerOf}`
                      : ""}
            </td>
            <td className="home score" width={"8%"}>
                {s.home.score}
            </td>
        </>
    );

    return (
        <div className="schedule" data-testid="schedule">
            <h2>{scheduleHeader}</h2>
            {schedule &&
                schedule.map(s => {
                    let monthOut: React.ReactNode;
                    let dayOut: React.ReactNode;

                    const date = new Date(s.date);

                    const gameMonth =
                        date.toLocaleString("default", {
                            month: "long",
                        }) +
                        " " +
                        date.getFullYear();

                    const gameDate = new Intl.DateTimeFormat("en-US", {
                        dateStyle: "long",
                    }).format(date);

                    if (gameMonth != newMonth) {
                        newMonth = gameMonth;

                        monthOut = <h3>{gameMonth}</h3>;
                    }

                    if (gameDate != newDay) {
                        newDay = gameDate;

                        dayOut = <h4>{gameDate}</h4>;
                    }

                    const gameTime = new Intl.DateTimeFormat("en-US", {
                        timeStyle: "short",
                    }).format(date);

                    return (
                        <Row key={s.id}>
                            <Col>
                                {monthOut}
                                {dayOut}
                                <Table
                                    className="scheduleGame"
                                    size="sm"
                                    borderless
                                >
                                    <tbody>
                                        {isXOrSmaller("md", breakpoint) ? (
                                            <>
                                                {s.isPlayoff ? (
                                                    <tr>
                                                        <td
                                                            colSpan={7}
                                                            className="bracketGame"
                                                        >
                                                            {s.champGameInfo
                                                                ? "Championship"
                                                                : "Consolation"}{" "}
                                                            Bracket Game #
                                                            {s.champGameInfo
                                                                ? s
                                                                      .champGameInfo
                                                                      ?.playoffGameId
                                                                : s.consGameInfo
                                                                      ?.playoffGameId}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    ""
                                                )}
                                                <tr
                                                    className={
                                                        s.isPlayoff
                                                            ? "playoffGame"
                                                            : undefined
                                                    }
                                                >
                                                    {timeAndLocation(
                                                        s,
                                                        gameTime,
                                                    )}
                                                </tr>
                                                <tr>{teamScoreInfo(s)}</tr>
                                                {(s.visitor.summary ||
                                                    s.home.summary) && (
                                                    <tr>
                                                        <td
                                                            colSpan={3}
                                                            className={classNames(
                                                                "visitorButtonCell",
                                                            )}
                                                        >
                                                            {s.visitor
                                                                .summary && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSummary(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [s.id]: s
                                                                                    .visitor
                                                                                    .summary,
                                                                            }),
                                                                        );
                                                                        setShowSummary(
                                                                            prev => {
                                                                                let show =
                                                                                    true;

                                                                                if (
                                                                                    prev?.[
                                                                                        s
                                                                                            .id
                                                                                    ] ===
                                                                                    true
                                                                                ) {
                                                                                    if (
                                                                                        summary?.[
                                                                                            s
                                                                                                .id
                                                                                        ] ===
                                                                                        s
                                                                                            .visitor
                                                                                            .summary
                                                                                    ) {
                                                                                        show =
                                                                                            false;
                                                                                    }
                                                                                }

                                                                                return {
                                                                                    ...prev,
                                                                                    [s.id]: show,
                                                                                };
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    Visitor Team
                                                                    Summary
                                                                </Button>
                                                            )}
                                                        </td>
                                                        <td></td>
                                                        <td
                                                            colSpan={3}
                                                            className={classNames(
                                                                "homeButtonCell",
                                                            )}
                                                        >
                                                            {s.home.summary && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSummary(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [s.id]: s
                                                                                    .home
                                                                                    .summary,
                                                                            }),
                                                                        );
                                                                        setShowSummary(
                                                                            prev => {
                                                                                let show =
                                                                                    true;

                                                                                if (
                                                                                    prev?.[
                                                                                        s
                                                                                            .id
                                                                                    ] ===
                                                                                    true
                                                                                ) {
                                                                                    if (
                                                                                        summary?.[
                                                                                            s
                                                                                                .id
                                                                                        ] ===
                                                                                        s
                                                                                            .home
                                                                                            .summary
                                                                                    ) {
                                                                                        show =
                                                                                            false;
                                                                                    }
                                                                                }

                                                                                return {
                                                                                    ...prev,
                                                                                    [s.id]: show,
                                                                                };
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    Home Team
                                                                    Summary
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                                <tr
                                                    className={classNames({
                                                        "d-none":
                                                            !showSummary?.[
                                                                s.id
                                                            ],
                                                        "d-table-row":
                                                            showSummary?.[s.id],
                                                    })}
                                                >
                                                    <td colSpan={8}>
                                                        {summary?.[s.id]}
                                                    </td>
                                                </tr>
                                            </>
                                        ) : s.isPlayoff ? (
                                            <>
                                                <tr>
                                                    {timeAndLocation(
                                                        s,
                                                        gameTime,
                                                    )}
                                                    <td
                                                        colSpan={7}
                                                        className="bracketGame"
                                                    >
                                                        {s.champGameInfo
                                                            ? "Championship"
                                                            : "Consolation"}{" "}
                                                        Bracket Game #
                                                        {s.champGameInfo
                                                            ? s.champGameInfo
                                                                  ?.playoffGameId
                                                            : s.consGameInfo
                                                                  ?.playoffGameId}
                                                    </td>
                                                </tr>
                                                <tr>{teamScoreInfo(s)}</tr>
                                                {(s.visitor.summary ||
                                                    s.home.summary) && (
                                                    <tr>
                                                        <td
                                                            className={classNames(
                                                                "timeAndLocation",
                                                            )}
                                                        ></td>
                                                        <td
                                                            colSpan={3}
                                                            className={classNames(
                                                                "visitorButtonCell",
                                                            )}
                                                        >
                                                            {s.visitor
                                                                .summary && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSummary(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [s.id]: s
                                                                                    .visitor
                                                                                    .summary,
                                                                            }),
                                                                        );
                                                                        setShowSummary(
                                                                            prev => {
                                                                                let show =
                                                                                    true;

                                                                                if (
                                                                                    prev?.[
                                                                                        s
                                                                                            .id
                                                                                    ] ===
                                                                                    true
                                                                                ) {
                                                                                    if (
                                                                                        summary?.[
                                                                                            s
                                                                                                .id
                                                                                        ] ===
                                                                                        s
                                                                                            .visitor
                                                                                            .summary
                                                                                    ) {
                                                                                        show =
                                                                                            false;
                                                                                    }
                                                                                }

                                                                                return {
                                                                                    ...prev,
                                                                                    [s.id]: show,
                                                                                };
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    Visitor Team
                                                                    Summary
                                                                </Button>
                                                            )}
                                                        </td>
                                                        <td></td>
                                                        <td
                                                            colSpan={3}
                                                            className={classNames(
                                                                "homeButtonCell",
                                                            )}
                                                        >
                                                            {s.home.summary && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSummary(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [s.id]: s
                                                                                    .home
                                                                                    .summary,
                                                                            }),
                                                                        );
                                                                        setShowSummary(
                                                                            prev => {
                                                                                let show =
                                                                                    true;

                                                                                if (
                                                                                    prev?.[
                                                                                        s
                                                                                            .id
                                                                                    ] ===
                                                                                    true
                                                                                ) {
                                                                                    if (
                                                                                        summary?.[
                                                                                            s
                                                                                                .id
                                                                                        ] ===
                                                                                        s
                                                                                            .home
                                                                                            .summary
                                                                                    ) {
                                                                                        show =
                                                                                            false;
                                                                                    }
                                                                                }

                                                                                return {
                                                                                    ...prev,
                                                                                    [s.id]: show,
                                                                                };
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    Home Team
                                                                    Summary
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                                <tr
                                                    className={classNames({
                                                        "d-none":
                                                            !showSummary?.[
                                                                s.id
                                                            ],
                                                        "d-table-row":
                                                            showSummary?.[s.id],
                                                    })}
                                                >
                                                    <td colSpan={8}>
                                                        {summary?.[s.id]}
                                                    </td>
                                                </tr>
                                            </>
                                        ) : (
                                            <React.Fragment>
                                                <tr>
                                                    {timeAndLocation(
                                                        s,
                                                        gameTime,
                                                    )}
                                                    {teamScoreInfo(s)}
                                                </tr>
                                                {(s.visitor.summary ||
                                                    s.home.summary) && (
                                                    <tr>
                                                        <td
                                                            className={classNames(
                                                                "timeAndLocation",
                                                            )}
                                                        ></td>
                                                        <td
                                                            colSpan={3}
                                                            className={classNames(
                                                                "visitorButtonCell",
                                                            )}
                                                        >
                                                            {s.visitor
                                                                .summary && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSummary(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [s.id]: s
                                                                                    .visitor
                                                                                    .summary,
                                                                            }),
                                                                        );
                                                                        setShowSummary(
                                                                            prev => {
                                                                                let show =
                                                                                    true;

                                                                                if (
                                                                                    prev?.[
                                                                                        s
                                                                                            .id
                                                                                    ] ===
                                                                                    true
                                                                                ) {
                                                                                    if (
                                                                                        summary?.[
                                                                                            s
                                                                                                .id
                                                                                        ] ===
                                                                                        s
                                                                                            .visitor
                                                                                            .summary
                                                                                    ) {
                                                                                        show =
                                                                                            false;
                                                                                    }
                                                                                }

                                                                                return {
                                                                                    ...prev,
                                                                                    [s.id]: show,
                                                                                };
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    Visitor Team
                                                                    Summary
                                                                </Button>
                                                            )}
                                                        </td>
                                                        <td></td>
                                                        <td
                                                            colSpan={3}
                                                            className={classNames(
                                                                "homeButtonCell",
                                                            )}
                                                        >
                                                            {s.home.summary && (
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={() => {
                                                                        setSummary(
                                                                            prev => ({
                                                                                ...prev,
                                                                                [s.id]: s
                                                                                    .home
                                                                                    .summary,
                                                                            }),
                                                                        );
                                                                        setShowSummary(
                                                                            prev => {
                                                                                let show =
                                                                                    true;

                                                                                if (
                                                                                    prev?.[
                                                                                        s
                                                                                            .id
                                                                                    ] ===
                                                                                    true
                                                                                ) {
                                                                                    if (
                                                                                        summary?.[
                                                                                            s
                                                                                                .id
                                                                                        ] ===
                                                                                        s
                                                                                            .home
                                                                                            .summary
                                                                                    ) {
                                                                                        show =
                                                                                            false;
                                                                                    }
                                                                                }

                                                                                return {
                                                                                    ...prev,
                                                                                    [s.id]: show,
                                                                                };
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    Home Team
                                                                    Summary
                                                                </Button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                                <tr
                                                    className={classNames({
                                                        "d-none":
                                                            !showSummary?.[
                                                                s.id
                                                            ],
                                                        "d-table-row":
                                                            showSummary?.[s.id],
                                                    })}
                                                >
                                                    <td colSpan={8}>
                                                        {summary?.[s.id]}
                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        )}
                                        {showButtons && s.rescheduleInfo && (
                                            <tr>
                                                <td
                                                    className="rescheduleInfo"
                                                    data-testid="rescheduleInfo"
                                                    colSpan={8}
                                                >
                                                    <b>Original</b>
                                                    <br />
                                                    {`${defaultDateFormat.format(
                                                        new Date(s.date),
                                                    )} @ ${defaultTimeFormat.format(
                                                        new Date(s.date),
                                                    )} / ${s.fieldName}${
                                                        s.diamond
                                                            ? ` #${s.diamond}`
                                                            : ""
                                                    }`}
                                                    <br />
                                                    <b
                                                        style={{
                                                            color: "#f00",
                                                        }}
                                                    >
                                                        Reschedule Suggestion
                                                    </b>
                                                    <br />
                                                    {`${defaultDateFormat.format(
                                                        new Date(
                                                            s.rescheduleInfo.date,
                                                        ),
                                                    )} @ ${defaultTimeFormat.format(
                                                        new Date(
                                                            s.rescheduleInfo.date,
                                                        ),
                                                    )} / ${
                                                        s.rescheduleInfo
                                                            .fieldName
                                                    }${
                                                        s.rescheduleInfo.diamond
                                                            ? ` Diamond: ${s.rescheduleInfo.diamond}`
                                                            : ""
                                                    }`}
                                                    {s.rescheduleInfo.note ? (
                                                        <>
                                                            <br />
                                                            Note:{" "}
                                                            {
                                                                s.rescheduleInfo
                                                                    .note
                                                            }
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                        {showButtons &&
                                            (selectedLeague ||
                                                (selectedTeam &&
                                                    canEditTeam(
                                                        selectedTeam,
                                                    ))) && (
                                                <GameActionButtons
                                                    game={s}
                                                    selectedTeam={selectedTeam}
                                                    selectedLeague={
                                                        selectedLeague
                                                    }
                                                    displayDeleteLink={
                                                        displayDeleteLink
                                                    }
                                                    handleDelete={handleDelete}
                                                    setSchedule={setSchedule!}
                                                    breakpoint={breakpoint}
                                                    scheduleType={scheduleType}
                                                />
                                            )}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    );
                })}
        </div>
    );
};

export default Schedule;
